import React from 'react';
import { CountryTemplate } from '../../components/CountryTemplate';

export const TanzaniaPage = () => {
  const tanzaniaData = {
    name: 'Tanzania',
    region: 'Africa',
    color: '#606090',
    metaTitle: 'Pharma & Nutraceutical Registration in Tanzania | TMDA | CTD Dossiers',
    metaDescription: 'Learn how to register pharmaceutical and nutraceutical products in Tanzania. TMDA requirements, CTD dossier expectations, timelines, documents, and regulatory pathways for a strongly regulated East African market.',
    
    hero: {
      title: 'Pharma & Nutraceutical Registration in Tanzania',
      subtitle: 'A regulated, fast-growing East African market governed by TMDA, with CTD dossier requirements and rising demand for chronic therapies and essential nutraceuticals.',
      cta: 'Start Your Tanzania Registration'
    },
    
    atAGlance: [
      { label: 'Population', value: '~67.4M' },
      { label: 'Income Level', value: 'Lower-middle income' },
      { label: 'Health Expenditure per Capita', value: '~USD 33-40' },
      { label: 'Health Expenditure (% of GDP)', value: '~3.8-4.1%' },
      { label: 'Urbanisation', value: '35% urban, 65% rural' },
      { label: 'Life Expectancy', value: '66 (men), 70 (women)' }
    ],
    
    marketOverview: {
      title: 'Market Overview: A Regulated, Import-Reliant Market',
      stats: [
        'Tanzania imports a significant share of finished pharmaceuticals via private sector + government tender channels',
        'TMDA (Tanzania Medicines and Medical Devices Authority) closely regulates all pharma and supplements',
        'India is one of the top suppliers of medicines to Tanzania (antibiotics, essential generics, injectables, OTC)',
        'Local industry exists but is limited in GMP scale - most advanced formulations are still imported',
        'Nutraceutical demand is emerging, especially for immunity, maternal health, paediatrics, and metabolic support'
      ],
      opportunities: [
        'Government tenders (Medical Stores Department - MSD)',
        'Private hospital networks (Aga Khan, Regency, Imam, etc.)',
        'Retail pharmacies expanding in Dar es Salaam, Arusha, Mwanza',
        'Strong acceptance of Indian generics'
      ]
    },
    
    diseaseburden: {
      title: 'Disease Landscape: What Drives Demand in Tanzania',
      subtitle: 'Tanzania faces high infectious disease burden alongside rapidly rising NCDs.',
      categories: [
        {
          name: 'Infectious Diseases',
          items: [
            'Malaria - Tanzania is among the top malaria-burden countries globally',
            'TB - High burden (~225 cases per 100,000)',
            'HIV - Significant prevalence (~4-5% nationally in adults)',
            'Portfolio: Antibiotics, antimalarials, antihypertensives, antidiabetics, analgesics, ORS, paediatrics'
          ]
        },
        {
          name: 'Non-Communicable Diseases',
          items: [
            'Hypertension & cardiovascular disease growing across urban population',
            'Diabetes prevalence ~3-4% but rising quickly with urbanisation',
            'Maternal health deficiencies remain significant',
            'Strong demand for iron, calcium, folate, prenatal multivitamins'
          ]
        },
        {
          name: 'Nutraceutical Opportunities',
          items: [
            'Maternal supplements essential',
            'Kids multivitamins for paediatric undernutrition',
            'Probiotics and omega-3',
            'Vitamin C and herbal immunity boosters',
            'Zinc, ORS, deworming remain essential'
          ]
        }
      ]
    },
    
    regulatoryFramework: {
      authority: 'Tanzania Medicines and Medical Devices Authority (TMDA)',
      description: 'Under Ministry of Health. TMDA is known for structured processes, high emphasis on GMP verification, and mandatory dossier review. Tanzania is part of EAC MRH (East African Community Medicines Regulatory Harmonisation) framework.',
      pharma: {
        timeline: 'Varies based on dossier completeness and GMP verification (one of the more stringent RA systems in East Africa)',
        requirements: [
          'CTD format mandatory (Modules 1-5)',
          'Module 1 - Tanzania-specific administrative docs',
          'Modules 2-5 - CTD technical documentation',
          'Full stability data for tropical climate',
          'GMP certificate (Indian GMP acceptable but may undergo scrutiny)',
          'TMDA may inspect manufacturing plants if required',
          'Product samples for quality verification',
          'Country-specific fees handled by importer or MAH'
        ],
        insight: 'Tanzania has one of the more stringent RA systems in East Africa. TMDA recognises GMP from SRAs but reserves right to inspect plants. Clarifications must be clean, scientific, and timely.'
      },
      nutra: {
        timeline: 'Comparatively faster when documentation is complete',
        requirements: [
          'Full formulation disclosure',
          'COA & microbiological limits',
          'GMP/quality certification from manufacturer',
          'Safety data for ingredients',
          'Clear classification to avoid medicinal claims',
          'Label review (English or Swahili)',
          'Claims limited to structure-function, not disease treatment',
          'Market Authorization / Registration number required'
        ],
        insight: 'Tanzania regulates supplements under food/dietary product laws, overseen by TMDA. Supplement approvals are comparatively faster when documentation is complete.'
      }
    },
    
    pharmaPathway: {
      title: 'Pharma Registration (CTD - TMDA)',
      steps: [
        'Local MAH Appointment - Tanzania-registered company must act as Marketing Authorization Holder',
        'Dossier Submission - CTD Format with Module 1 (Tanzania-specific) and Modules 2-5 (technical)',
        'Full stability data for tropical climate inclusion',
        'GMP Verification - TMDA recognises SRA GMP, scrutiny for Indian GMP',
        'TMDA reserves right to inspect manufacturing plants if required',
        'Samples + Lab Tests - TMDA requires samples for quality verification',
        'Query Handling - Clean, scientific, timely clarifications required',
        'Approval & Registration Certificate - After technical & administrative compliance',
        'Renewable registration with annual retention fees'
      ]
    },
    
    nutraPathway: {
      title: 'Nutraceutical & Dietary Supplement Registration (TMDA)',
      steps: [
        'Classification & product category determination',
        'Full formulation disclosure preparation',
        'COA & microbiological limits documentation',
        'GMP/quality certification from manufacturer',
        'Safety data for ingredients compilation',
        'Clear classification to avoid medicinal claims',
        'Label review (English or Swahili)',
        'Dossier + safety review submission',
        'Labelling & artwork compliance verification',
        'Query resolution',
        'Market Authorization / Registration number issuance'
      ]
    },
    
    requiredDocs: {
      'Pharma (CTD)': [
        'Administrative forms (Module 1)',
        'CTD Module 2-5 documents',
        'GMP certificate',
        'CoPP (if available)',
        'Stability data (Zone IVb)',
        'API/DMF documentation',
        'Process validation data',
        'Product specs + COA',
        'Labels, inserts (English / Swahili)'
      ],
      'Nutraceuticals': [
        'Full quantitative formula',
        'COA (finished product + key ingredients)',
        'GMP certificate',
        'Safety justification',
        'Packaging artwork (TMDA compliant)',
        'Claims justification',
        'Certificate of Free Sale (CFS) when required'
      ]
    },
    
    whyThisCountry: {
      title: 'Why Tanzania Should Be Part of Your Expansion Strategy',
      reasons: [
        'Large population (67M+) with rapid urbanisation',
        'High infectious disease burden ensures sustained essential drug demand',
        'Growing middle class - NCD market expanding',
        'Strong acceptance of Indian generics',
        'Regional position: Tanzania is part of the EAC block',
        'TMDA is structured & predictable for compliant products',
        'Low local manufacturing capacity for advanced formulations',
        'Multi-country submissions benefit from EAC participation'
      ],
      bestPortfolio: [
        'Pharma: Antibiotics, antimalarials, antihypertensives, antidiabetics, analgesics, ORS, paediatrics',
        'Nutra: Maternal supplements, kids multivitamins, probiotics, omega-3, vitamin C',
        'Herbal immunity boosters and essential micronutrients'
      ]
    },
    
    howWeHelp: {
      title: 'How TheApostrophe Supports Tanzania Submissions',
      services: [
        'Product-market suitability analysis',
        'CTD dossier preparation & technical writing',
        'TMDA artwork & label compliance',
        'Nutraceutical classification & documentation',
        'Importer coordination',
        'Query/deficiency responses',
        'Post-approval lifecycle support (renewals, variations, retention)'
      ]
    },
    
    faqs: [
      {
        question: 'Which authority regulates pharmaceuticals in Tanzania?',
        answer: 'TMDA (Tanzania Medicines & Medical Devices Authority) under the Ministry of Health.'
      },
      {
        question: 'Does Tanzania require CTD format?',
        answer: 'Yes. CTD is mandatory for pharmaceuticals.'
      },
      {
        question: 'Are Indian GMP certificates accepted?',
        answer: 'Yes, but TMDA may request additional verification and reserves the right to inspect manufacturing plants if required.'
      },
      {
        question: 'What languages are allowed on labels?',
        answer: 'English or Swahili, depending on product type.'
      },
      {
        question: 'Do supplements require registration?',
        answer: 'Yes. TMDA regulates supplements and issues market authorization numbers.'
      }
    ]
  };

  return <CountryTemplate countryData={tanzaniaData} />;
};
